import { useEffect, useState } from 'react'

import { AxiosInstance } from 'axios'

import {
  isBCCProvider,
  isBCMProvider,
  isBCTProvider,
  isDAProviderOfferingMeds,
  isDAProviderOfferingTherapy,
  ProviderInfo,
  RiskFactorsRequest,
  RiskFactorsResponse,
} from '@lyrahealth-inc/shared-app-logic'

import { hasPastSI } from '../../highAlert/utils'
import { HIGH_RISK_CONDITION_KEYS } from '../../providers/constants'

export interface UseShouldShowHighRiskModalDirectBookingProps {
  provider?: ProviderInfo
  outsideOnboarding?: boolean
  userId?: string
  axiosInstance: AxiosInstance
  shouldDisplayHighRiskModalForDirectBooking?: boolean
}

export const useShouldShowHighRiskModalDirectBooking = ({
  provider,
  outsideOnboarding,
  userId,
  axiosInstance,
  shouldDisplayHighRiskModalForDirectBooking,
}: UseShouldShowHighRiskModalDirectBookingProps): { shouldShowModal: boolean; isLoading: boolean } => {
  const [hasRecentHighRisk, setHasRecentHighRisk] = useState<boolean>(false)
  // Start with loading=true if we need to make the API call
  const shouldFetchRiskFactors = !!outsideOnboarding && !!userId && !!shouldDisplayHighRiskModalForDirectBooking
  const [isLoading, setIsLoading] = useState<boolean>(shouldFetchRiskFactors)

  useEffect(() => {
    if (!outsideOnboarding || !userId || !shouldDisplayHighRiskModalForDirectBooking) {
      setIsLoading(false)
      return
    }
    const fetchRiskFactors = async () => {
      setIsLoading(true)
      try {
        const request: RiskFactorsRequest = {
          lyra_id: userId,
          monthsToLookBack: 6,
        }

        const response = await axiosInstance.post<RiskFactorsResponse>(`/trs/v1/risk_factors`, request)
        setHasRecentHighRisk(hasPastSI(response.data))
      } catch {
        setHasRecentHighRisk(false)
      } finally {
        setIsLoading(false)
      }
    }

    fetchRiskFactors()
  }, [userId, outsideOnboarding, axiosInstance, shouldDisplayHighRiskModalForDirectBooking])

  if (isLoading || !provider) {
    return { shouldShowModal: false, isLoading }
  }

  const isProviderCoach = provider.program_taxonomy
    ? isBCCProvider({ programTaxonomy: provider.program_taxonomy })
    : false

  const doesProviderExcludeHighRisk =
    (isDAProviderOfferingTherapy({ programTaxonomy: provider.program_taxonomy || [] }) ||
      isDAProviderOfferingMeds({ programTaxonomy: provider.program_taxonomy || [] }) ||
      isBCTProvider({ programTaxonomy: provider.program_taxonomy || [] }) ||
      isBCMProvider({ programTaxonomy: provider.program_taxonomy || [] })) &&
    provider.negative_conditions?.some((condition) => HIGH_RISK_CONDITION_KEYS.includes(condition))

  const shouldShowModal = hasRecentHighRisk && (isProviderCoach || doesProviderExcludeHighRisk) && !!outsideOnboarding

  return { shouldShowModal, isLoading: false }
}
